import * as core from 'cw-ui-core';
import AsioSelfService from './Api/AsioApi/AsioSelfService';
import { FEEDBACK_DIALOG_ID } from 'src/components/AsioEnablement/Feedback/FeedbackDialogContainer';
import { ASIO_ENABLEMENT_DIALOG_ID } from 'src/components/AsioEnablement/AsioEnablementDialog/AsioEnablementDialogContainer';
import { CountryCodeMapping, CurrencyCodeMapping, bgCountryCodeMapping } from 'src/utils';

export const getRegion = async () => {
  const dispatch = core.Store().dispatch;
  const state = core.Store().getState();
  const region = core.getModuleCache(state.module, ['partner', 'region']);
  if (region === undefined || region === null) {
    const result = await AsioSelfService.getPartnerRegion();
    dispatch(core.setModuleCache(['partner', 'region'], result.response));
  }
};

export const checkEligibility = async () => {
  const dispatch = core.Store().dispatch;
  const result = await AsioSelfService.checkEligibility();
  if (result?.isSuccess) {
    dispatch(core.setModuleCache(['eligibility'], result.response));
  } else {
    dispatch(core.setModuleCache(['eligibility'], null));
  }
};

export const provisionAsio = async () => {
  let companyName;
  const state = core.Store().getState();
  const asioProductId = core.getModuleConfig(state.module, ['asioProductId']);
  const asioMemberCode = core.getScreenData(
    state.screen,
    ['memberCode'],
    ASIO_ENABLEMENT_DIALOG_ID
  );
  const asioMemberName = core.getScreenData(state.screen, ['memberName'], ASIO_ENABLEMENT_DIALOG_ID);
  const userEmail = core.getUserEmail(state.oidc);
  const partnerModule = core.getModuleCache(state.module, ['partner']);
  const partner = partnerModule ? partnerModule.toJS() : undefined;
  const firstName = core.getUserFirstName(state.oidc);
  let partnerCompany = core.getModuleCache(state.module, [
    'partner',
    'companyName'
  ]);
  if (partnerCompany) {
    companyName = partnerCompany.replace(/[^a-zA-Z0-9-]/g, '');
  }

  const currencyCode = CurrencyCodeMapping.find(
    code => code.name === partner.currency
  );
  const currency = currencyCode ? currencyCode.code : 'USDOLLAR';
  const countryCode = CountryCodeMapping.find(
    code => code.name === partner.country
  );
  const country = countryCode ? countryCode.code : 'US';
  let provisioningDetails = {
    productId: asioProductId,
    email: userEmail,
    agreementAcknowledged: true,
    signupData: {
      confirmLogonEmail: userEmail,
      country: partner.country,
      cwPartnerId: partner.id,
      logonEmail: userEmail,
      memberBillingCurrency: currency,
      memberCode: asioMemberCode ? asioMemberCode : companyName,
      memberCountry: country,
      memberCreatedBy: userEmail,
      memberName: asioMemberName ? asioMemberName : partner.companyName,
      telephoneNo: partner.phoneNumber,
      mobileNo: partner.phoneNumber,
      salesForceReferenceAccountID: partner.sfAccountNumber,
      userFirstName: firstName
    },
    instanceData: {
      paymentStatus :{
        identifier: "free"
      }
    }
  };
  let result = await AsioSelfService.provisionAsio(provisioningDetails);
  return result;
};

export const submitFeedback = async (caseSubject = null, description = null) => {
  const state = core.Store().getState();
  const caseConstant = 'Platform Self-service in CW Home Error';
  const userId = core.getUserId(state.oidc);
  const subjectIssue = core.getScreenData(
    state.screen,
    ['issueTitle'],
    FEEDBACK_DIALOG_ID
  );
  const caseDescription = core.getScreenData(
    state.screen,
    ['issueSummary'],
    FEEDBACK_DIALOG_ID
  );
  let feedbackData = {
    useCase: caseConstant,
    contactSsoId: userId,
    subject: caseSubject || subjectIssue,
    description: description || caseDescription
  };
  let result = await AsioSelfService.submitFeedback(feedbackData);
  return result;
};

const fetchDataAndDispatch = async (fetchFunction, cacheKey) => {
  const dispatch = core.Store().dispatch;
  try {
    const result = await fetchFunction();
    if (!result.isSuccess) {
      throw new Error(result.error);
    }

    dispatch(core.setModuleCache(cacheKey, result.response));
  } catch (error) {
    core.CwLog.error(error);
  }
};

export const getWhatsNextData = async () => {
  await fetchDataAndDispatch(AsioSelfService.getWhatsNextData, ['asioSelfService', 'whatsNextData']);
};

export const getVideoTutorialsData = async () => {
  await fetchDataAndDispatch(AsioSelfService.getVideoTutorialsData, ['asioSelfService', 'videoAndTutorialsData']);
};

export const hasPsaPoweredByAsioEntitlement = async () => {
  const result = await AsioSelfService.hasPsaPoweredByAsioEntitlement();
  return result;
};

export const hasValidPsaInstance = async () => {
  const state = core.Store().getState();
  const productsCache = core.getModuleCache(state.module, ['partnerProducts']);
  const products = productsCache ? productsCache.toJS() : [];
  const filteredProducts = products.filter(product =>
    product.name &&
    product.name.toLowerCase().includes('manage') &&
    product.hasActiveInstance &&
    product.installType &&
    product.installType.toLowerCase() === 'production' &&
    product.loginCompanyName
  );

  const missingFields = [];
  // Instead of filteredProducts, check all products with 'manage' in the name
  const manageProducts = products.filter(
    product => product.name && product.name.toLowerCase().includes('manage')
  );

  manageProducts.forEach(product => {
    const missing = [];
    if (!product?.name) missing.push('name');
    if (!product?.installType) missing.push('installType');
    if (!product?.loginCompanyName) missing.push('loginCompanyName');
    if (product?.installType?.toLowerCase() !== 'production') missing.push('Valid installType - (Production)');
    if (product?.hasActiveInstance !== true) missing.push('Instance status should be Active');
    // Only add to missingFields if any required field is missing
    if (missing.length > 0) {
      missingFields.push({
        description: `Product with instanceId ${product.instanceId} is missing: ${missing.join(', ')}`
      });
    }
  });

  let region = core.getModuleCache(state.module, ['partner', 'region']);
  if (region && region.toLowerCase() === 'australia') {
    region = 'aus';
  } else if (region && region.toLowerCase() === 'north america') {
    region = 'na';
  } else if (region && region.toLowerCase() === 'europe') {
    region = 'eu';
  } else {
    region = 'other';
  }

  if (filteredProducts.length === 0) {
    return { missingFields, instanceId: null };
  }

  const result = hasSingleValidPsaInstance(filteredProducts, region, missingFields);
  return result;
};

const hasSingleValidPsaInstance = (filteredProducts, region, missingFields) => {
  const state = core.Store().getState();
  const environment = core.getModuleConfig(state.module, ['build']);
  let validProducts = [];

  if (environment === 'local' || environment === 'dev') {
    validProducts = filteredProducts.filter(
      product => product.domain?.toLowerCase() === 'staging.connectwisedev.com'
    );
  } else if (environment === 'prod') {
    validProducts = filteredProducts.filter(product => {
      const domainPrefix = product?.domain?.split('.')[0]?.toLowerCase();
      return domainPrefix === region.toLowerCase();
    });
  }

  if (validProducts.length !== 1 && !validProducts[0]?.IsPsaClientEnabled) {
    missingFields.push({
      description:
        ' Either the Classic PSA instance domain does not match the partner region, or there are multiple Classic PSA valid instances.'
    });
    return { missingFields, instanceId: null };
  }

  return { instanceId: validProducts[0]?.instanceId };
};

export const provisionPsaPackage = async instanceId => {
  let companyName;
  const state = core.Store().getState();
  const asioProductId = core.getModuleConfig(state.module, ['asioProductId']);
  const bgProductId = core.getModuleConfig(state.module, ['bgProductId']);
  const asioMemberName = core.getScreenData(state.screen, ['memberName'], ASIO_ENABLEMENT_DIALOG_ID);
  const asioMemberCode = core.getScreenData(
    state.screen,
    ['memberCode'],
    ASIO_ENABLEMENT_DIALOG_ID
  );
  const bgCompanyName = core.getScreenData(
    state.screen,
    ['companyName'],
    ASIO_ENABLEMENT_DIALOG_ID
  );
  const bgSubDomain = core.getScreenData(
    state.screen,
    ['subDomain'],
    ASIO_ENABLEMENT_DIALOG_ID
  );
  const regionRaw = core.getModuleCache(state.module, ['partner', 'region']);
  const partnerRegion = regionRaw === 'North America' ? 'America' : regionRaw;
  const userEmail = core.getUserEmail(state.oidc);
  const partnerModule = core.getModuleCache(state.module, ['partner']);
  const partner = partnerModule ? partnerModule.toJS() : undefined;
  const firstName = core.getUserFirstName(state.oidc);
  let partnerCompany = core.getModuleCache(state.module, [
    'partner',
    'companyName'
  ]);
  if (partnerCompany) {
    companyName = partnerCompany.replace(/[^a-zA-Z0-9-]/g, '');
  }

  const currencyCode = CurrencyCodeMapping.find(
    code => code.name === partner.currency
  );
  const currency = currencyCode ? currencyCode.code : 'USDOLLAR';
  const countryCode = CountryCodeMapping.find(
    code => code.name === partner.country
  );
  const country = countryCode ? countryCode.code : 'US';
  const bgCountryCode = bgCountryCodeMapping.find(
    code => code.name === partner.country
  );
  const bgCountry = bgCountryCode ? bgCountryCode.code : 'US';
  let provisioningDetails = {
    packageProvisionedBy: userEmail,
    packageId: "",
    email: userEmail,
    agreementAcknowledged: true,
    instanceData: {
      paymentStatus :{
        identifier: "free"
      }
    },
    signupDatas: {
      [asioProductId]: {
        memberName: asioMemberName ? asioMemberName : partner.companyName,
        memberCode: asioMemberCode ? asioMemberCode : companyName,
        salesForceReferenceAccountID: partner.sfAccountNumber,
        country: partner.country,
        memberBillingCurrency: currency,
        logonEmail: userEmail,
        confirmLogonEmail: userEmail,
        userFirstName: firstName,
        telephoneNo: partner.phoneNumber,
        mobileNo: partner.phoneNumber,
        memberCreatedBy: userEmail,
        productName: "",
        cwPartnerId: partner.id,
        memberCountry: country
      },
      [bgProductId]: {
        companyName: bgCompanyName || partner.companyName,
        countryCode: bgCountry,
        region: partnerRegion,
        subDomain: bgSubDomain || companyName,
        brightGaugePlan:  {
          planName: ""
        }
      }
    }
  };
  let result = await AsioSelfService.provisionPsaPackage(provisioningDetails, instanceId);
  return result;
};

export const checkSubDomainStatus = async subDomain => {
  return await AsioSelfService.checkSubDomainStatus(subDomain);
};

export const provisionAutomatePackage = async () => {
  let companyName;
  const state = core.Store().getState();
  const asioProductId = core.getModuleConfig(state.module, ['asioProductId']);
  const userEmail = core.getUserEmail(state.oidc);
  const partnerModule = core.getModuleCache(state.module, ['partner']);
  const partner = partnerModule ? partnerModule.toJS() : undefined;
  const firstName = core.getUserFirstName(state.oidc);
  const asioMemberName = core.getScreenData(state.screen, ['memberName'], ASIO_ENABLEMENT_DIALOG_ID);
  const asioMemberCode = core.getScreenData(
    state.screen,
    ['memberCode'],
    ASIO_ENABLEMENT_DIALOG_ID
  );
  let partnerCompany = core.getModuleCache(state.module, [
    'partner',
    'companyName'
  ]);
  if (partnerCompany) {
    companyName = partnerCompany.replace(/[^a-zA-Z0-9-]/g, '');
  }

  const currencyCode = CurrencyCodeMapping.find(
    code => code.name === partner.currency
  );
  const currency = currencyCode ? currencyCode.code : 'USDOLLAR';
  const countryCode = CountryCodeMapping.find(
    code => code.name === partner.country
  );
  const country = countryCode ? countryCode.code : 'US';
  let provisioningDetails = {
    packageProvisionedBy: userEmail,
    packageId: "",
    email: userEmail,
    agreementAcknowledged: true,
    instanceData: {
      paymentStatus :{
        identifier: "free"
      }
    },
    signupDatas: {
      [asioProductId]: {
        memberName: asioMemberName ? asioMemberName : partner.companyName,
        memberCode: asioMemberCode ? asioMemberCode : companyName,
        salesForceReferenceAccountID: partner.sfAccountNumber,
        country: partner.country,
        memberBillingCurrency: currency,
        logonEmail: userEmail,
        confirmLogonEmail: userEmail,
        userFirstName: firstName,
        telephoneNo: partner.phoneNumber,
        mobileNo: partner.phoneNumber,
        memberCreatedBy: userEmail,
        productName: "",
        cwPartnerId: partner.id,
        memberCountry: country
      }
    }
  };
  let result = await AsioSelfService.provisionAutomatePackage(provisioningDetails);
  return result;
};

export const validateSignupData = async () => {
 let companyName;
  const state = core.Store().getState();
  const asioProductId = core.getModuleConfig(state.module, ['asioProductId']);
  const asioMemberCode = core.getScreenData(
    state.screen,
    ['memberCode'],
    ASIO_ENABLEMENT_DIALOG_ID
  );
  const asioMemberName = core.getScreenData(state.screen, ['memberName'], ASIO_ENABLEMENT_DIALOG_ID);
  const userEmail = core.getUserEmail(state.oidc);
  const partnerModule = core.getModuleCache(state.module, ['partner']);
  const partner = partnerModule ? partnerModule.toJS() : undefined;
  const firstName = core.getUserFirstName(state.oidc);
  let partnerCompany = core.getModuleCache(state.module, [
    'partner',
    'companyName'
  ]);
  if (partnerCompany) {
    companyName = partnerCompany.replace(/[^a-zA-Z0-9-]/g, '');
  }

  const currencyCode = CurrencyCodeMapping.find(
    code => code.name === partner.currency
  );
  const currency = currencyCode ? currencyCode.code : 'USDOLLAR';
  const countryCode = CountryCodeMapping.find(
    code => code.name === partner.country
  );
  const country = countryCode ? countryCode.code : 'US';
  let data = {
    partnerId: partner.id,
    productId: asioProductId,
    email: userEmail,
    agreementAcknowledged: true,
    signupData: {
      confirmLogonEmail: userEmail,
      country: partner.country,
      cwPartnerId: partner.id,
      logonEmail: userEmail,
      memberBillingCurrency: currency,
      memberCode: asioMemberCode ? asioMemberCode : companyName,
      memberCountry: country,
      memberCreatedBy: userEmail,
      memberName: asioMemberName ? asioMemberName : partnerCompany,
      telephoneNo: partner.phoneNumber,
      mobileNo: partner.phoneNumber,
      salesForceReferenceAccountID: partner.sfAccountNumber,
      userFirstName: firstName
    }
  };
  
  let result = await AsioSelfService.validateSignupData(data);
  return result;
};